/**
 * The aggregate turnover that decides how many HSN digits this taxpayer must
 * report (X-09 Stage 0).
 *
 * **Why this control exists.** `hsn.digits` compares a line's HSN against the
 * digits the taxpayer's band requires, and it reads the figure off the client's
 * taxpayer group. The column has existed since migration 0016; nothing could
 * ever write it, so the rule has only ever been able to hold. One real August
 * went out with 111,266 lines under 4-digit headings and nothing said whether
 * that was enough.
 *
 * **Three states, never collapsed into one.** A PAN match SUGGESTS a group and
 * writes nothing (D-16) — until a person confirms, the group's turnover is not
 * this client's, and this card says so rather than showing a figure that does
 * not apply to the taxpayer being looked at.
 */
import { Alert, Button, Card, Input } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, type GroupForClient, groups } from '../lib/api'

/** Paise in, rupees out — money is integer paise on the wire, always. */
function rupees(paise: number | null): string {
  if (paise === null) return ''
  return (paise / 100).toFixed(2)
}

export function TurnoverCard({ clientId, pan }: { clientId: string; pan: string | null }) {
  const { can } = useEvaluation()
  const editable = can(CLIENT, 'can_update')

  const [state, setState] = useState<GroupForClient | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [amount, setAmount] = useState('')
  const [fy, setFy] = useState('')

  const load = useCallback(async () => {
    try {
      const body = (await groups.forClient(clientId)).data
      setState(body)
      setAmount(rupees(body.group?.previous_fy_turnover_paise ?? null))
      setFy(body.group?.turnover_fy ?? '')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The taxpayer group could not be read.')
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await load()
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  if (!state) return null

  const group = state.group

  return (
    <Card className="p-4">
      <p className="gst-panel-h mb-3">Aggregate turnover</p>

      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}

      {state.state === null && (
        <>
          <p className="gst-meta mb-3">
            No taxpayer group exists for this PAN. HSN digit sufficiency cannot be
            checked until one does — the rule holds rather than guessing a band.
          </p>
          {editable && pan && (
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  const made = await groups.create({ pan, display_name: pan })
                  await groups.addMember(made.data.group.id, {
                    client_id: clientId,
                    suggested_from_pan: false,
                  })
                })
              }
            >
              Create a group for {pan}
            </Button>
          )}
        </>
      )}

      {state.state === 'suggested' && group && (
        <>
          <Alert tone="warning" className="mb-3">
            A group for this PAN exists ({group.display_name}) but this client has
            not been joined to it. Its turnover is <strong>not</strong> this
            client&rsquo;s until someone confirms that.
          </Alert>
          {editable && (
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(() =>
                  groups.addMember(group.id, {
                    client_id: clientId,
                    suggested_from_pan: true,
                  }),
                )
              }
            >
              Confirm this client belongs to {group.display_name}
            </Button>
          )}
        </>
      )}

      {state.state === 'confirmed' && group && (
        <>
          <p className="gst-meta mb-3">
            Group <strong>{group.display_name}</strong> · PAN {group.pan}
            {group.previous_fy_turnover_paise === null && (
              <> — no figure entered, so HSN digit sufficiency still holds.</>
            )}
          </p>
          {editable ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Previous FY turnover (₹)"
                  value={amount}
                  inputMode="decimal"
                  onChange={(e) => {
                    setAmount(e.target.value)
                    setError(null) // N31: a refusal goes when the input it was about changes
                  }}
                />
                <Input
                  label="Financial year"
                  placeholder="2026-27"
                  value={fy}
                  onChange={(e) => {
                    setFy(e.target.value)
                    setError(null)
                  }}
                />
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={busy || !amount.trim() || !fy.trim()}
                  onClick={() =>
                    void act(() =>
                      groups.setTurnover(group.id, {
                        // Rupees in the field, paise on the wire. Rounded, not
                        // truncated: a figure typed to the paisa must survive.
                        previous_fy_turnover_paise: Math.round(Number(amount) * 100),
                        turnover_fy: fy.trim(),
                      }),
                    )
                  }
                >
                  {busy ? 'Saving…' : 'Save turnover'}
                </Button>
                {group.previous_fy_turnover_paise !== null && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void act(() =>
                        groups.setTurnover(group.id, {
                          previous_fy_turnover_paise: null,
                          turnover_fy: null,
                        }),
                      )
                    }
                  >
                    Clear
                  </Button>
                )}
              </div>
            </>
          ) : (
            <p className="gst-meta">
              {group.previous_fy_turnover_paise === null
                ? 'Not entered.'
                : `₹${rupees(group.previous_fy_turnover_paise)} for ${group.turnover_fy}`}
            </p>
          )}
        </>
      )}
    </Card>
  )
}
